import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

const auroraWave = keyframes`
  0% {
    transform: rotate(0deg) scale(0.95);
    filter: blur(50px) hue-rotate(0deg);
  }
  50% {
    transform: rotate(180deg) scale(1.15);
    filter: blur(65px) hue-rotate(30deg);
  }
  100% {
    transform: rotate(360deg) scale(0.95);
    filter: blur(50px) hue-rotate(0deg);
  }
`;

const BackgroundContainer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
`;

/* 3D Multi-dimensional Aurora Mesh */
const AuroraMesh = styled.div`
  position: absolute;
  top: -20%;
  right: -10%;
  width: 700px;
  height: 700px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(133, 76, 230, 0.35) 0%,
    rgba(192, 38, 211, 0.2) 35%,
    rgba(56, 189, 248, 0.12) 60%,
    transparent 80%
  );
  filter: blur(55px);
  animation: ${auroraWave} 18s ease-in-out infinite alternate;
  opacity: 0.85;

  @media (max-width: 960px) {
    width: 450px;
    height: 450px;
    right: 50%;
    transform: translateX(50%);
  }
`;

const SecondaryAurora = styled.div`
  position: absolute;
  bottom: -10%;
  left: 10%;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(56, 189, 248, 0.15) 0%,
    rgba(133, 76, 230, 0.18) 40%,
    transparent 75%
  );
  filter: blur(60px);
  animation: ${auroraWave} 24s ease-in-out infinite reverse;
  opacity: 0.6;
`;

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.75;
  z-index: 2;
`;

const HeroBgAnimation = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    let width = (canvas.width = canvas.parentElement.offsetWidth);
    let height = (canvas.height = canvas.parentElement.offsetHeight);

    const handleResize = () => {
      if (!canvas || !canvas.parentElement) return;
      width = canvas.width = canvas.parentElement.offsetWidth;
      height = canvas.height = canvas.parentElement.offsetHeight;
    };

    window.addEventListener('resize', handleResize);

    // Neural Network Particles Configuration
    const particleCount = width < 768 ? 32 : 55;
    const maxDistance = width < 768 ? 95 : 125;
    const particles = [];

    const colors = ['#854CE6', '#be1adb', '#38bdf8', '#c084fc'];

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.55,
        vy: (Math.random() - 0.5) * 0.55,
        radius: Math.random() * 2 + 1.2,
        color: colors[Math.floor(Math.random() * colors.length)],
        alpha: Math.random() * 0.6 + 0.3,
      });
    }

    // Animation Loop
    const render = () => {
      ctx.clearRect(0, 0, width, height);

      // Draw neural connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < maxDistance) {
            const lineAlpha = (1 - dist / maxDistance) * 0.35;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(133, 76, 230, ${lineAlpha})`;
            ctx.lineWidth = 0.9;
            ctx.stroke();
          }
        }
      }

      // Draw neural nodes / particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y < 0) p.y = height;
        if (p.y > height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 8;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <BackgroundContainer>
      {/* 3D Glowing Aurora Clouds */}
      <AuroraMesh />
      <SecondaryAurora />

      {/* Subtle Neural Network Canvas */}
      <Canvas ref={canvasRef} />
    </BackgroundContainer>
  );
};

export default HeroBgAnimation;